import React, { useContext, useEffect, useState } from 'react';
import { Alert, Button, Icon } from '@syneto/compass-react';
import { Link } from 'react-router-dom';
import { StoreContext } from '../../../../contexts/StoreContext';
import { ServiceContext } from '../../../../contexts/ServiceContext';
import { BANNER_KEYS } from '../../../../services/BannerDismissal';

const INVENTORY_PATH = '/inventory';

export const PartnerPreviewBanner = () => {
	const { partnerPreviewService, bannerDismissalService, accessService } = useContext(ServiceContext);
	const { state } = useContext(StoreContext);
	const { loggedInUser } = state;
	const canAccessInventory = accessService.hasPermissionOnAnyResource('appliance.list');

	const [visible, setVisible] = useState(false);

	useEffect(() => {
		const loadBannerVisibility = async () => {
			try {
				const [participation, dismissals] = await Promise.all([
					partnerPreviewService.getParticipation(loggedInUser.company.id),
					bannerDismissalService.getDismissals(loggedInUser.company.id),
				]);

				// dismissals contains every banner the user has dismissed, not just this one -
				// a legacy-OS-version-only dismissal must not hide this banner.
				const dismissed = dismissals.some((dismissal) => { return dismissal.bannerKey === BANNER_KEYS.PARTNER_PREVIEW; });

				setVisible(Boolean(participation?.enabled) && !dismissed);
			} catch (error) {
				// The banner is informational, so an unreachable endpoint hides it rather than
				// failing the whole Dashboard.
				setVisible(false);
			}
		};

		loadBannerVisibility();
	}, []);

	// Shared by the "Dismiss" button and the close icon: both persist the same dismissal.
	const handleDismiss = async () => {
		try {
			await bannerDismissalService.dismissBanner(loggedInUser.company.id, BANNER_KEYS.PARTNER_PREVIEW);
			setVisible(false);
		} catch (error) {
			// Keep the banner up: hiding it would suggest a dismissal that was never persisted,
			// so it would come back on the next login anyway. The dismissal is idempotent, so
			// the user can simply try again.
		}
	};

	if (!visible) {
		return null;
	}

	return (
		<Alert variant="primary" className="mb-3" dismissible closeLabel="Close" onClose={handleDismiss}>
			<div className="d-flex justify-content-between align-items-center">
				<div className="d-flex align-items-center">
					<Icon name="fas fa-info-circle" className="text-primary me-3" style={{ fontSize: '32px' }} />
					<div>
						<div className="fw-bold">Your company has been enrolled in the Partner Preview Program.</div>
						<div>To access Preview builds, enable the Preview update channel for individual machines from the Inventory page.</div>
					</div>
				</div>
				<div className="d-flex align-items-center flex-shrink-0 text-nowrap ms-4">
					{canAccessInventory && (
						/* Navigating to another page is a link, not a button: compass's Button forces
						role="button" onto whatever it renders, which would hide that from assistive
						technology, so the design system's button styling is applied to the Link. */
						<Link to={INVENTORY_PATH} className="btn btn-primary me-2">Go to Inventory</Link>
					)}
					<Button role="secondary" onClick={handleDismiss}>Dismiss</Button>
				</div>
			</div>
		</Alert>
	);
};
